import { useSearchParams } from "react-router";
import { useEffect, useRef, useState } from "react";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import Button from "../../components/ui/button/Button";

import type { UserConfirmResp, UserConfirmValues } from '../../types/confirmationUserTypes';

const CONFIRMATION_EMAIL = gql`
  mutation ConfirmRegistrationWithToken($confirmationToken: String!) {
    confirmRegistrationWithToken(confirmationToken: $confirmationToken) {
      user {
        id
        email
      }
      success
      errors
      message
    }
  }
`   
   
const ConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const calledRef = useRef(false);
  const [email, setEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [message, setMessage] = useState("");
  const [confirmRegistrationWithToken, { loading, error }] = useMutation<UserConfirmResp, UserConfirmValues>(CONFIRMATION_EMAIL);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const confirmationToken = searchParams.get("confirmationToken");

    if (!confirmationToken) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setErrorMsg("No token in URL");
      return;
    }

    let mounted = true;

    (async () => {
      try {
        const { data } = await confirmRegistrationWithToken({
          variables: { confirmationToken },
        });
        
        const responseData = data?.confirmRegistrationWithToken;

        if (responseData?.success && responseData?.user) {
          setEmail(responseData?.user.email);
        } else {
          setErrorMsg(responseData?.errors?.join(", ") ?? "Unknown error");
        }
        if (responseData?.message) setMessage(responseData.message);
      } catch (e) {
        if (!mounted) return;
        const message = e instanceof Error ? e.message : String(e);
        setErrorMsg(message);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [searchParams, confirmRegistrationWithToken]);

  const renderResultMessage = () => {
    if (loading) return (
      <div>
        <div className="min-h-screen rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
          <div className="mx-auto w-full max-w-[630px] text-center">
            <h3 className="mb-4 font-semibold text-gray-800 text-theme-xl dark:text-white/90 sm:text-2xl">
              Confirmation...
            </h3>
          </div>
        </div>
      </div>  
    );
    if (errorMsg || error) return (
      <div>
        <div className="min-h-screen rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
          <div className="mx-auto w-full max-w-[630px] text-center">
            <h3 className="mb-4 font-semibold text-gray-800 text-theme-xl dark:text-white/90 sm:text-2xl">
              {errorMsg}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 sm:text-base">
              {message}
            </p>
          </div>
        </div>
      </div>  
    );
    if (email) return (
      <div>
        <div className="min-h-screen rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
          <div className="mx-auto w-full max-w-[630px] text-center">
            <h3 className="mb-4 font-semibold text-gray-800 text-theme-xl dark:text-white/90 sm:text-2xl">
              Account Confirmed!
            </h3>

            <p className="text-sm text-gray-500 dark:text-gray-400 sm:text-base">
              {message}
            </p>
            <br />
            <Button 
              size="md" 
              variant="primary"
              onClick={() => window.location.href = '/signin'}
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return renderResultMessage();
};

export default ConfirmationPage;
